import {
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  Event as EventIcon,
  ExpandMore as ExpandMoreIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import downloadStatisticsService from "../../services/downloadStatisticsService";

const DownloadStatisticsPage = () => {
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalDownloads: 0,
    completedDownloads: 0,
    failedDownloads: 0,
    inProgressDownloads: 0,
    totalPages: 0,
    currentPage: 1,
  });
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    productId: "",
    versionId: "",
    userId: "",
    status: "all",
    page: 1,
    limit: 50,
    sortBy: "created_at",
    sortOrder: "DESC",
  });
  const [userDetailsDialog, setUserDetailsDialog] = useState({
    open: false,
    userId: null,
    userEmail: "",
  });
  const [userDownloads, setUserDownloads] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [users, setUsers] = useState([]);
  const [exportingUserEvents, setExportingUserEvents] = useState(null);
  const [exportingAllEvents, setExportingAllEvents] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchDownloadStatistics();
    fetchUsers();
  }, [filters]);

  const fetchUsers = async () => {
    try {
      const response = await api.get("/admin/users");
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchDownloadStatistics = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== "all" && value !== "") {
          if (key === "startDate" || key === "endDate") {
            queryParams.append(key, value.toISOString());
          } else {
            queryParams.append(key, value);
          }
        }
      });

      const response = await api.get(
        `/admin/download-statistics?${queryParams}`,
      );
      setDownloads(response.data.downloads);
      setStats(response.data.stats);
    } catch (error) {
      console.error("Error fetching download statistics:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId, userEmail) => {
    try {
      setLoadingUserDetails(true);
      const response = await api.get(
        `/admin/user-download-statistics/${userId}`,
      );
      setUserDownloads(response.data.downloads);
      setUserStats(response.data.stats);
      setUserDetailsDialog({
        open: true,
        userId,
        userEmail,
      });
    } catch (error) {
      console.error("Error fetching user details:", error);
    } finally {
      setLoadingUserDetails(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 1, // Reset to first page when filters change
    }));
  };

  const handleUserSelectChange = (event, newValue) => {
    setSelectedUser(newValue);
    setFilters((prev) => ({
      ...prev,
      userId: newValue ? newValue.id : "",
      page: 1,
    }));
  };

  const handlePageChange = (event, newPage) => {
    setFilters((prev) => ({
      ...prev,
      page: newPage + 1,
    }));
  };

  const handleRowsPerPageChange = (event) => {
    setFilters((prev) => ({
      ...prev,
      limit: parseInt(event.target.value),
      page: 1,
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "failed":
        return "error";
      default:
        return "warning";
    }
  };

  const formatDuration = (startDate, endDate) => {
    if (!endDate) return "N/A";
    const duration = new Date(endDate) - new Date(startDate);
    return `${Math.round(duration / 1000)}s`;
  };

  const getSuccessRate = () => {
    if (stats.totalDownloads === 0) return 0;
    return ((stats.completedDownloads / stats.totalDownloads) * 100).toFixed(1);
  };

  const resetFilters = () => {
    setFilters({
      startDate: null,
      endDate: null,
      productId: "",
      versionId: "",
      userId: "",
      status: "all",
      page: 1,
      limit: 50,
      sortBy: "created_at",
      sortOrder: "DESC",
    });
    setSelectedUser(null);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await downloadStatisticsService.exportDownloadStatistics(filters);
    } catch (error) {
      console.error("Error exporting data:", error);
    } finally {
      setExporting(false);
    }
  };

  const handleExportUserEvents = async (userId) => {
    try {
      setExportingUserEvents(userId);
      const queryParams = new URLSearchParams();

      if (filters.startDate) {
        queryParams.append("startDate", filters.startDate.toISOString());
      }
      if (filters.endDate) {
        queryParams.append("endDate", filters.endDate.toISOString());
      }

      // Get the token for authentication
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Create the correct URL
      const url = `/admin/user-event-logs/${userId}/export?${queryParams}`;

      // Use api instance which already has the correct base URL and interceptors
      const response = await api.get(url, {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Create download link
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `user-${userId}-events-${
        new Date().toISOString().split("T")[0]
      }.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error exporting user events:", error);
      alert("Error exporting user events: " + error.message);
    } finally {
      setExportingUserEvents(null);
    }
  };

  const handleExportAllEvents = async () => {
    try {
      setExportingAllEvents(true);
      const queryParams = new URLSearchParams();

      if (filters.startDate) {
        queryParams.append("startDate", filters.startDate.toISOString());
      }
      if (filters.endDate) {
        queryParams.append("endDate", filters.endDate.toISOString());
      }
      if (filters.userId) {
        queryParams.append("userId", filters.userId);
      }

      // Get the token for authentication
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Create the correct URL
      const url = `/admin/event-logs/export?${queryParams}`;

      // Use api instance which already has the correct base URL and interceptors
      const response = await api.get(url, {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Create download link
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `event-logs-${
        new Date().toISOString().split("T")[0]
      }.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error exporting all events:", error);
      alert("Error exporting all events: " + error.message);
    } finally {
      setExportingAllEvents(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: { xs: 2, sm: 3 },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            mr: { sm: 2 },
            mb: { xs: 1, sm: 0 },
            alignSelf: { xs: "flex-start", sm: "center" },
          }}
          size={window.innerWidth < 600 ? "small" : "medium"}
        >
          Back
        </Button>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Download Statistics
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={resetFilters}
            startIcon={<RefreshIcon />}
          >
            Reset Filters
          </Button>
          <Button
            variant="outlined"
            onClick={handleExport}
            startIcon={<ExportIcon />}
            disabled={loading || exporting}
          >
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
          <Button
            variant="outlined"
            onClick={handleExportAllEvents}
            startIcon={<EventIcon />}
            disabled={loading || exportingAllEvents}
          >
            {exportingAllEvents ? "Exporting..." : "Export Event Logs"}
          </Button>
          <Button
            variant="contained"
            onClick={fetchDownloadStatistics}
            startIcon={<RefreshIcon />}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Downloads
                  </Typography>
                  <Typography variant="h4">{stats.totalDownloads}</Typography>
                </Box>
                <DownloadIcon sx={{ fontSize: 40, color: "primary.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Completed
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {stats.completedDownloads}
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, color: "success.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Failed
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {stats.failedDownloads}
                  </Typography>
                </Box>
                <TrendingDownIcon sx={{ fontSize: 40, color: "error.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Success Rate
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {getSuccessRate()}%
                  </Typography>
                </Box>
                <AssessmentIcon sx={{ fontSize: 40, color: "info.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Accordion
        defaultExpanded
        sx={{ bgcolor: "background.default", border: "none" }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Filters</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(date) => handleFilterChange("startDate", date)}
                  slotProps={{ textField: { size: "small" } }}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate}
                  onChange={(date) => handleFilterChange("endDate", date)}
                  slotProps={{ textField: { size: "small" } }}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="started">Started</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={users}
                getOptionLabel={(option) => option.email}
                value={selectedUser}
                onChange={handleUserSelectChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{ width: "300px" }}
                    label="Select User"
                    fullWidth
                  />
                )}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Downloads Table */}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>IP Address</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {downloads.map((download, index) => (
              <TableRow
                key={download.id}
                hover
                sx={{
                  borderBottom: "1px solid",
                  borderBottomColor: "divider",
                  "& td": {
                    py: 2,
                  },
                }}
              >
                <TableCell>
                  {new Date(download.created_at).toLocaleString()}
                </TableCell>
                <TableCell>
                  <Tooltip title={`User ID: ${download.User.id}`}>
                    <span style={{ cursor: "pointer" }}>
                      {download.User.email}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>{download.ProductVersion.Product.name}</TableCell>
                <TableCell>{download.ProductVersion.version}</TableCell>
                <TableCell>
                  <Chip
                    label={download.status}
                    color={getStatusColor(download.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{download.ip_address || "N/A"}</TableCell>
                <TableCell>
                  {formatDuration(download.started_at, download.completed_at)}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      onClick={() =>
                        fetchUserDetails(download.User.id, download.User.email)
                      }
                      disabled={loadingUserDetails}
                      size="small"
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleExportUserEvents(download.User.id)}
                      disabled={exportingUserEvents === download.User.id}
                      size="small"
                    >
                      <EventIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {downloads.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No downloads found matching the current filters.
                  </Alert>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={stats.totalDownloads}
        rowsPerPage={filters.limit}
        page={stats.currentPage - 1}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      {/* User Details Dialog */}
      <Dialog
        open={userDetailsDialog.open}
        onClose={() =>
          setUserDetailsDialog({ open: false, userId: null, userEmail: "" })
        }
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Download Details for {userDetailsDialog.userEmail}
        </DialogTitle>
        <DialogContent>
          {loadingUserDetails ? (
            <LinearProgress sx={{ my: 2 }} />
          ) : userStats ? (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Total Downloads
                      </Typography>
                      <Typography variant="h6">
                        {userStats.totalDownloads}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Completed
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {userStats.completedDownloads}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Failed
                      </Typography>
                      <Typography variant="h6" color="error.main">
                        {userStats.failedDownloads}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Unique Products
                      </Typography>
                      <Typography variant="h6">
                        {userStats.uniqueProducts}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <TableContainer component={Paper} elevation={1}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>Version</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Duration</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userDownloads.map((download, index) => (
                      <TableRow
                        key={download.id}
                        hover
                        sx={{
                          borderBottom: "1px solid",
                          borderBottomColor: "divider",
                          "& td": {
                            py: 1.5,
                          },
                        }}
                      >
                        <TableCell>
                          {new Date(download.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {download.ProductVersion.Product.name}
                        </TableCell>
                        <TableCell>{download.ProductVersion.version}</TableCell>
                        <TableCell>
                          <Chip
                            label={download.status}
                            color={getStatusColor(download.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {formatDuration(
                            download.started_at,
                            download.completed_at,
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleExportUserEvents(userDetailsDialog.userId)}
            disabled={exportingUserEvents === userDetailsDialog.userId}
            startIcon={<EventIcon />}
          >
            {exportingUserEvents === userDetailsDialog.userId
              ? "Exporting Events..."
              : "Export User Events"}
          </Button>
          <Button
            onClick={() =>
              setUserDetailsDialog({ open: false, userId: null, userEmail: "" })
            }
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DownloadStatisticsPage;
